import * as XLSX from "xlsx";
import { formatAllergenTitlesForExport } from "./menuImport";
import { fetchSideMenuItems, fetchAllergens } from "../../services/menuApi";

/**
 * Build a full menu workbook (Categories / Subcategories / Sides / Allergens / Items).
 * @returns {Promise<import("xlsx").WorkBook>}
 */
export async function buildMenuWorkbook(menu, tenantServerUrl) {
  if (!menu || !Array.isArray(menu.categories)) {
    throw new Error("No menu categories to export");
  }

  const categoriesSheetData = menu.categories.map(({ subcategories, ...rest }) => rest);

  const subcategoriesSheetData = menu.categories.flatMap((cat) =>
    (cat.subcategories || []).map(({ items, ...subRest }) => ({
      category_id: cat.id,
      category_name: cat.name,
      category_code: cat.code,
      ...subRest,
    }))
  );

  const itemsSheetData = menu.categories.flatMap((cat) =>
    (cat.subcategories || []).flatMap((sub) =>
      (sub.items || []).map((item) => {
        const raw = { ...(item.raw || {}) };
        delete raw.allergens_resolved;
        delete raw.sides_resolved;
        const allergens =
          formatAllergenTitlesForExport(item) ||
          String(raw.allergens_titles || "")
            .split("--")
            .map((s) => s.trim())
            .filter(Boolean)
            .join("--");
        return {
          category_id: cat.id,
          category_name: cat.name,
          category_code: cat.code,
          subcategory_id: sub.id,
          subcategory_name: sub.name,
          subcode: sub.subcode || raw.subcode,
          ...raw,
          allergens,
        };
      })
    )
  );

  let sidesSheetData = [];
  try {
    if (tenantServerUrl) {
      const sides = await fetchSideMenuItems(tenantServerUrl, { universal: "all" });
      sidesSheetData = sides.map((side) => ({
        item: side.item,
        price: side.price,
        description: side.description,
        quantity: side.quantity,
        visibility: side.visibility,
        universal: side.universal,
      }));
    }
  } catch (e) {
    console.warn("[menuBackup] sides export skipped:", e);
  }

  let allergensSheetData = [];
  try {
    if (tenantServerUrl) {
      const allergens = await fetchAllergens(tenantServerUrl);
      allergensSheetData = allergens.map((row) => ({
        title: row.title,
        description: row.description,
        icon: row.icon && row.icon !== "none.jpg" ? row.icon : "",
      }));
    }
  } catch (e) {
    console.warn("[menuBackup] allergens export skipped:", e);
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(categoriesSheetData),
    "Categories"
  );
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(subcategoriesSheetData),
    "Subcategories"
  );
  if (sidesSheetData.length) {
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(sidesSheetData),
      "Sides"
    );
  }
  if (allergensSheetData.length) {
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(allergensSheetData),
      "Allergens"
    );
  }
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(itemsSheetData),
    "Items"
  );
  return wb;
}

export function workbookToArrayBuffer(wb) {
  return XLSX.write(wb, { bookType: "xlsx", type: "array" });
}

function stamp() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(
    d.getHours()
  )}${p(d.getMinutes())}${p(d.getSeconds())}`;
}

function downloadBlob(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
}

/**
 * Zip Excel + SQL menu backup and trigger download.
 * Does NOT clear the menu — caller should clear after this resolves.
 */
export async function downloadMenuBackupZip({
  menu,
  tenantServerUrl,
  sqlPayload,
  zipFilename,
}) {
  const JSZip = (await import("jszip")).default;
  const wb = await buildMenuWorkbook(menu, tenantServerUrl);
  const xlsxBytes = workbookToArrayBuffer(wb);
  const sqlText =
    typeof sqlPayload === "string" ? sqlPayload : sqlPayload?.sql || "";
  if (!sqlText) throw new Error("SQL dump is empty");

  const when = stamp();
  const zip = new JSZip();
  zip.file(`menu_${when}.xlsx`, xlsxBytes);
  zip.file(`menu_${when}.sql`, sqlText);

  const tableLines = Object.entries(sqlPayload?.tables || {})
    .map(([name, count]) => `  - ${name}: ${count} row(s)`)
    .join("\n");

  zip.file(
    "README.txt",
    [
      "Table Mate — menu backup",
      `Generated: ${sqlPayload?.generated_at || new Date().toISOString()}`,
      "",
      "Contents:",
      `  - menu_${when}.xlsx  — Categories, Subcategories, Sides, Allergens, Items`,
      `  - menu_${when}.sql   — Full SQL dump (DROP/CREATE/INSERT) for menu tables`,
      "",
      "SQL tables included:",
      tableLines || "  (see .sql file)",
      "",
      "Restore SQL via phpMyAdmin Import or mysql CLI.",
      "Restore Excel via Menu → Import (structure/library) after categories exist,",
      "or prefer the SQL file for a full structural restore.",
      "",
    ].join("\n")
  );

  const blob = await zip.generateAsync({ type: "blob" });
  const name = zipFilename || `menu_backup_${when}.zip`;
  downloadBlob(blob, name);
  return { filename: name, when };
}
